// Central configuration: navigation, resource schemas, field definitions.
import {
  LayoutDashboard, Users, Target, FileText, RefreshCw, LifeBuoy, AlertTriangle,
  ShieldCheck, FolderOpen, Building2, Activity, CheckSquare, BarChart3, Upload,
  Settings, ScrollText, Anchor, FileStack, Mail, GitMerge, AlertOctagon, Megaphone, DollarSign, Briefcase, PhoneCall, Gauge,
  CalendarDays, Contact, UserRound, TrendingUp, BookOpen,
} from "lucide-react";

export const NAV = [
  { section: null, items: [{ label: "Command Center", to: "/", icon: LayoutDashboard, testid: "nav-command-center" }] },
  {
    section: "Agency Operations", collapsible: true, defaultOpen: true,
    items: [
      { label: "Calendar", to: "/calendar", icon: CalendarDays, testid: "nav-calendar" },
      { label: "Expiring Policies", to: "/expiring-policies", icon: AlertTriangle, testid: "nav-expiring" },
      { label: "Book of Business", to: "/book", icon: BookOpen, testid: "nav-book" },
    ],
  },
  {
    section: "Business Owner Intelligence", collapsible: true,
    items: [
      { label: "BOI Dashboard", to: "/boi-dashboard", icon: Gauge, testid: "nav-boi-dashboard" },
      { label: "Daily Call Queue", to: "/call-queue", icon: PhoneCall, testid: "nav-call-queue" },
      { label: "Relationships", to: "/relationships", icon: Briefcase, testid: "nav-relationships" },
      { label: "BOI Reports", to: "/boi-reports", icon: BarChart3, testid: "nav-boi-reports" },
    ],
  },
  {
    section: "Relationships", collapsible: true, defaultOpen: true,
    items: [
      { label: "Clients", to: "/clients", icon: Users, testid: "nav-clients" },
      { label: "Prospects", to: "/prospects", icon: UserRound, testid: "nav-prospects" },
      { label: "Contacts", to: "/contacts", icon: Contact, testid: "nav-contacts" },
      { label: "Individuals", to: "/individuals", icon: UserRound, testid: "nav-individuals" },
      { label: "Sales & Marketing", to: "/marketing", icon: Megaphone, testid: "nav-marketing" },
      { label: "Leads & Opportunities", to: "/leads", icon: Target, testid: "nav-leads" },
    ],
  },
  {
    section: "Book of Business", collapsible: true,
    items: [
      { label: "Policies", to: "/policies", icon: FileText, testid: "nav-policies" },
      { label: "Renewals", to: "/renewals", icon: RefreshCw, testid: "nav-renewals" },
      { label: "Claims", to: "/claims", icon: AlertTriangle, testid: "nav-claims" },
      { label: "Certificates", to: "/certificates", icon: ShieldCheck, testid: "nav-certificates" },
      { label: "Commissions", to: "/commissions", icon: DollarSign, testid: "nav-commissions" },
    ],
  },
  {
    section: "Management", collapsible: true,
    items: [
      { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    ],
  },
  {
    section: "Reporting", collapsible: true,
    items: [
      { label: "Reporting Center", to: "/reporting", icon: BarChart3, testid: "nav-reporting" },
      { label: "Premium Change", to: "/premium-change", icon: TrendingUp, testid: "nav-premium-change" },
    ],
  },
  {
    section: "Service", collapsible: true,
    items: [
      { label: "Service Center", to: "/service-cases", icon: LifeBuoy, testid: "nav-service" },
      { label: "Activities", to: "/activities", icon: Activity, testid: "nav-activities" },
      { label: "Tasks", to: "/tasks", icon: CheckSquare, testid: "nav-tasks" },
      { label: "Email", to: "/email", icon: Mail, testid: "nav-email" },
    ],
  },
  {
    section: "Operations", collapsible: true,
    items: [
      { label: "Carriers & Markets", to: "/carriers", icon: Building2, testid: "nav-carriers" },
      { label: "Forms Library", to: "/forms", icon: FileStack, testid: "nav-forms" },
      { label: "Documents", to: "/documents", icon: FolderOpen, testid: "nav-documents" },
      { label: "Reports", to: "/reports", icon: BarChart3, testid: "nav-reports" },
      { label: "Imports", to: "/imports", icon: Upload, testid: "nav-imports" },
    ],
  },
  {
    section: "System", collapsible: true,
    items: [
      { label: "Conflict Center", to: "/conflicts", icon: GitMerge, testid: "nav-conflicts" },
      { label: "Error Center", to: "/errors", icon: AlertOctagon, testid: "nav-errors", minLevel: 80 },
      { label: "Administration", to: "/administration", icon: Settings, testid: "nav-admin", minLevel: 80 },
      { label: "Agency Tools", to: "/agency-tools", icon: FolderOpen, testid: "nav-agency-tools", minLevel: 80 },
      { label: "Management", to: "/management", icon: BarChart3, testid: "nav-management", minLevel: 80 },
      { label: "Audit Trail", to: "/audit", icon: ScrollText, testid: "nav-audit", minLevel: 80 },
    ],
  },
];

export const Icons = { Anchor };

// ---------- badge colors ----------
export function badgeClass(value) {
  const v = String(value || "").toLowerCase();
  if (["active", "in force", "renewed", "bound", "issued", "verified", "approved", "completed", "closed"].includes(v))
    return "bg-seagreen/10 text-seagreen border-seagreen/30";
  if (["urgent", "critical", "cancelled", "lost", "high", "cancellation pending", "nonrenewal pending"].includes(v))
    return "bg-seared/10 text-seared border-seared/30";
  if (["pending", "waiting", "open", "requested", "in progress", "marketing", "quoted", "reported", "not started", "pending approval"].includes(v))
    return "bg-seaamber/10 text-seaamber border-seaamber/30";
  return "bg-ice text-atlantic border-sky/50";
}

const OWNER = { lookup: "owners" };
const CARRIER = { lookup: "carriers" };
const PARTY = { lookup: "parties" };

// ---------- resource schemas ----------
// type: text|textarea|number|date|select|switch|party|carrier|owner
export const RESOURCES = {
  leads: {
    key: "leads", title: "Leads", singular: "Lead", route: "/leads",
    columns: [
      { key: "lead_ref", label: "Ref" }, { key: "name", label: "Name" },
      { key: "source", label: "Source" }, { key: "lines_of_interest", label: "Lines" },
      { key: "status", label: "Stage", badge: true }, { key: "next_action_date", label: "Next Action", date: true },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "person_or_business", label: "Type", type: "select", options: { static: ["Person", "Business"] } },
      { key: "source", label: "Source", type: "text" },
      { key: "email", label: "Email", type: "text" }, { key: "phone", label: "Phone", type: "text" },
      { key: "address", label: "Address", type: "text" },
      { key: "lines_of_interest", label: "Lines of Interest", type: "text" },
      { key: "insurance_need", label: "Insurance Need", type: "textarea" },
      { key: "status", label: "Stage", type: "select", options: { config: "lead_stages" } },
      { key: "primary_producer_id", label: "Assigned Producer", type: "producer", ...OWNER },
      { key: "next_action", label: "Next Action", type: "text" },
      { key: "next_action_date", label: "Next Action Date", type: "date" },
      { key: "contact_permission", label: "Contact Permission", type: "switch" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  opportunities: {
    key: "opportunities", title: "Opportunities", singular: "Opportunity", route: "/opportunities",
    columns: [
      { key: "opp_ref", label: "Ref" }, { key: "name", label: "Opportunity" },
      { key: "lines_requested", label: "Lines" }, { key: "expected_premium", label: "Exp. Premium", money: true },
      { key: "stage", label: "Stage", badge: true }, { key: "target_close_date", label: "Target Close", date: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "name", label: "Opportunity Name", type: "text", required: true },
      { key: "personal_or_commercial", label: "Type", type: "select", options: { static: ["Personal", "Commercial"] } },
      { key: "source", label: "Source", type: "text" },
      { key: "producer", label: "Producer", type: "text" },
      { key: "account_manager", label: "Account Manager", type: "text" },
      { key: "expected_premium", label: "Expected Premium", type: "number" },
      { key: "target_close_date", label: "Target Close Date", type: "date" },
      { key: "lines_requested", label: "Lines Requested", type: "text" },
      { key: "stage", label: "Stage", type: "select", options: { config: "opportunity_stages" } },
      { key: "probability", label: "Probability (%)", type: "number" },
      { key: "next_action", label: "Next Action", type: "text" },
      { key: "loss_reason", label: "Loss Reason", type: "select", options: { config: "loss_reasons" } },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  policies: {
    key: "policies", title: "Policies", singular: "Policy", route: "/policies", detail: true,
    columns: [
      { key: "policy_number", label: "Policy #" }, { key: "line", label: "Line" },
      { key: "carrier_name", label: "Carrier" }, { key: "named_insured", label: "Named Insured" },
      { key: "premium", label: "Premium", money: true }, { key: "expiration_date", label: "Expires", date: true },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "policy_number", label: "Policy Number", type: "text", required: true },
      { key: "line", label: "Line of Business", type: "select", options: { configLines: true } },
      { key: "carrier_name", label: "Carrier", type: "carrier", ...CARRIER },
      { key: "named_insured", label: "Named Insured", type: "text" },
      { key: "status", label: "Status", type: "select", options: { config: "policy_statuses" } },
      { key: "premium", label: "Annual Premium", type: "number" },
      { key: "billing_type", label: "Billing Type", type: "select", options: { static: ["Agency Bill", "Direct Bill"] } },
      { key: "effective_date", label: "Effective Date", type: "date" },
      { key: "expiration_date", label: "Expiration Date", type: "date" },
      { key: "producer", label: "Producer", type: "text" },
      { key: "account_manager", label: "Account Manager", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  renewals: {
    key: "renewals", title: "Renewal Center", singular: "Renewal", route: "/renewals",
    columns: [
      { key: "renewal_ref", label: "Ref" }, { key: "policy_number", label: "Policy #" },
      { key: "carrier_name", label: "Carrier" }, { key: "line", label: "Line" },
      { key: "expiration_date", label: "Expires", date: true }, { key: "contact_status", label: "Contact", badge: true },
      { key: "outcome", label: "Outcome", badge: true }, { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "policy_number", label: "Policy Number", type: "text" },
      { key: "carrier_name", label: "Carrier", type: "carrier", ...CARRIER },
      { key: "line", label: "Line", type: "select", options: { configLines: true } },
      { key: "expiration_date", label: "Expiration Date", type: "date" },
      { key: "owner_user_id", label: "Producer", type: "owner", ...OWNER },
      { key: "service_tier", label: "Service Tier", type: "select", options: { config: "service_tiers" } },
      { key: "contact_status", label: "Contact Status", type: "select", options: { static: ["Not Started", "Attempted", "Contacted", "In Discussion", "Decision Received"] } },
      { key: "marketing_decision", label: "Marketing Decision", type: "select", options: { static: ["Renew As-Is", "Remarket", "Rewrite", "Review Coverage"] } },
      { key: "status", label: "Status", type: "select", options: { static: ["Open", "In Progress", "Waiting", "Closed"] } },
      { key: "outcome", label: "Outcome", type: "select", options: { config: "renewal_outcomes" } },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  claims: {
    key: "claims", title: "Claims Advocacy", singular: "Claim", route: "/claims",
    columns: [
      { key: "claim_ref", label: "Ref" }, { key: "carrier_claim_number", label: "Carrier Claim #" },
      { key: "claim_type", label: "Type" }, { key: "carrier", label: "Carrier" },
      { key: "date_of_loss", label: "Date of Loss", date: true }, { key: "severity", label: "Severity", badge: true },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "policy_id", label: "Policy (id)", type: "text" },
      { key: "carrier_claim_number", label: "Carrier Claim Number", type: "text" },
      { key: "carrier", label: "Carrier", type: "carrier", ...CARRIER },
      { key: "claim_type", label: "Claim Type", type: "text" },
      { key: "date_of_loss", label: "Date of Loss", type: "date" },
      { key: "date_reported", label: "Date Reported", type: "date" },
      { key: "severity", label: "Severity", type: "select", options: { config: "claim_severities" } },
      { key: "status", label: "Status", type: "select", options: { config: "claim_statuses" } },
      { key: "adjuster", label: "Adjuster", type: "text" },
      { key: "adjuster_phone", label: "Adjuster Phone", type: "text" },
      { key: "adjuster_email", label: "Adjuster Email", type: "text" },
      { key: "advocacy_notes", label: "Advocacy Notes", type: "textarea" },
      { key: "outcome", label: "Outcome", type: "text" },
    ],
  },
  certificates: {
    key: "certificates", title: "Certificates & Evidence", singular: "Certificate", route: "/certificates",
    columns: [
      { key: "cert_ref", label: "Ref" }, { key: "certificate_holder", label: "Certificate Holder" },
      { key: "operations", label: "Operations" }, { key: "delivery_method", label: "Delivery" },
      { key: "issue_date", label: "Issued", date: true }, { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "policy_id", label: "Policy (id)", type: "text" },
      { key: "certificate_holder", label: "Certificate Holder", type: "text", required: true },
      { key: "operations", label: "Operations / Description", type: "textarea" },
      { key: "required_wording", label: "Required Wording", type: "textarea" },
      { key: "additional_insured_request", label: "Additional Insured Requested", type: "switch" },
      { key: "waiver_request", label: "Waiver of Subrogation Requested", type: "switch" },
      { key: "non_standard_wording", label: "Non-Standard Wording", type: "switch" },
      { key: "rush", label: "Rush Request", type: "switch" },
      { key: "delivery_method", label: "Delivery Method", type: "select", options: { static: ["Email", "Portal", "Mail", "Fax"] } },
      { key: "status", label: "Status", type: "select", options: { static: ["Requested", "Pending Approval", "Approved", "Issued"] } },
      { key: "owner_user_id", label: "Producer", type: "owner", ...OWNER },
    ],
  },
  service_cases: {
    key: "service_cases", title: "Service Center", singular: "Service Case", route: "/service-cases",
    columns: [
      { key: "case_number", label: "Case #" }, { key: "subject", label: "Subject" },
      { key: "case_type", label: "Type" }, { key: "priority", label: "Priority", badge: true },
      { key: "due_date", label: "Due", date: true }, { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "subject", label: "Subject", type: "text", required: true },
      { key: "case_type", label: "Case Type", type: "select", options: { config: "service_case_types" } },
      { key: "priority", label: "Priority", type: "select", options: { config: "case_priorities" } },
      { key: "status", label: "Status", type: "select", options: { config: "case_statuses" } },
      { key: "owner_user_id", label: "Producer", type: "owner", ...OWNER },
      { key: "policy_id", label: "Related Policy (id)", type: "text" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "sla_target", label: "SLA Target Date", type: "date" },
      { key: "requester", label: "Requester", type: "text" },
      { key: "source", label: "Source", type: "select", options: { static: ["Phone", "Email", "Portal", "Walk-in", "Internal"] } },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  activities: {
    key: "activities", title: "Activities & Communications", singular: "Activity", route: "/activities",
    columns: [
      { key: "timestamp", label: "When", datetime: true }, { key: "activity_type", label: "Type", badge: true },
      { key: "direction", label: "Direction" }, { key: "subject", label: "Subject" },
      { key: "owner", label: "Owner" }, { key: "outcome", label: "Outcome" },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "activity_type", label: "Type", type: "select", options: { config: "activity_types" }, required: true },
      { key: "direction", label: "Direction", type: "select", options: { static: ["Inbound", "Outbound", "Internal"] } },
      { key: "subject", label: "Subject", type: "text", required: true },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "owner", label: "Owner", type: "text" },
      { key: "policy_id", label: "Related Policy (id)", type: "text" },
      { key: "next_action", label: "Next Action", type: "text" },
      { key: "outcome", label: "Outcome", type: "text" },
      { key: "visibility", label: "Visibility", type: "select", options: { static: ["Internal", "Client-Visible"] } },
    ],
  },
  tasks: {
    key: "tasks", title: "Tasks", singular: "Task", route: "/tasks",
    columns: [
      { key: "title", label: "Task" }, { key: "owner", label: "Owner" },
      { key: "priority", label: "Priority", badge: true }, { key: "due_date", label: "Due", date: true },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "owner", label: "Owner", type: "text", required: true },
      { key: "priority", label: "Priority", type: "select", options: { config: "case_priorities" } },
      { key: "status", label: "Status", type: "select", options: { static: ["Open", "In Progress", "Waiting", "Completed", "Cancelled"] } },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "completed_date", label: "Completed Date", type: "date" },
    ],
  },
  carriers: {
    key: "carriers", title: "Carriers & Markets", singular: "Carrier / Market", route: "/carriers",
    columns: [
      { key: "name", label: "Carrier / MGA" }, { key: "type", label: "Type", badge: true },
      { key: "appointment_status", label: "Appointment", badge: true }, { key: "contact_person", label: "Contact" },
      { key: "market_access_type", label: "Access" }, { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "name", label: "Carrier / MGA Name", type: "text", required: true },
      { key: "type", label: "Type", type: "select", options: { static: ["Carrier", "MGA", "Wholesaler / MGA", "Program", "Vendor"] } },
      { key: "status", label: "Status", type: "select", options: { static: ["Active", "Restricted", "Inactive"] } },
      { key: "appointment_status", label: "Appointment Status", type: "select", options: { static: ["Appointed", "In Process", "Not Appointed"] } },
      { key: "contact_person", label: "Contact Person", type: "text" },
      { key: "phone", label: "Phone", type: "text" }, { key: "email", label: "Email", type: "text" },
      { key: "portal_url", label: "Portal URL", type: "text" },
      { key: "market_access_type", label: "Market Access", type: "select", options: { static: ["Direct", "Wholesaler", "Cluster / Aggregator"] } },
      { key: "underwriting_appetite", label: "Underwriting Appetite", type: "textarea" },
      { key: "territory", label: "Territory", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  documents: {
    key: "documents", title: "Documents", singular: "Document", route: "/documents",
    columns: [
      { key: "filename", label: "Filename" }, { key: "category", label: "Category", badge: true },
      { key: "classification", label: "Classification" }, { key: "source", label: "Source" },
      { key: "created_at", label: "Added", datetime: true },
    ],
    fields: [
      { key: "party_id", label: "Client", type: "party", ...PARTY },
      { key: "filename", label: "Filename", type: "text", required: true },
      { key: "category", label: "Category", type: "select", options: { config: "document_categories" } },
      { key: "policy_id", label: "Related Policy (id)", type: "text" },
      { key: "dropbox_path", label: "Dropbox Path", type: "text" },
      { key: "classification", label: "Classification", type: "select", options: { static: ["Standard", "Confidential", "Restricted"] } },
      { key: "source", label: "Source", type: "select", options: { static: ["Manual", "Import", "Carrier", "Client"] } },
      { key: "version", label: "Version", type: "text" },
    ],
  },
};

export const CLIENT_FIELDS = {
  person: [
    { key: "display_name", label: "Full Name", type: "text", required: true },
    { key: "first_name", label: "First Name", type: "text" },
    { key: "middle_name", label: "Middle Name", type: "text" },
    { key: "last_name", label: "Last Name", type: "text" },
    { key: "preferred_name", label: "Preferred Name", type: "text" },
    { key: "date_of_birth", label: "Date of Birth", type: "date" },
    { key: "occupation", label: "Occupation", type: "text" },
    { key: "employer", label: "Employer", type: "text" },
    { key: "email", label: "Email", type: "text" },
    { key: "phone", label: "Phone", type: "text" },
    { key: "address_street", label: "Street Address", type: "text" },
    { key: "address_city", label: "City", type: "text" },
    { key: "address_state", label: "State", type: "text" },
    { key: "address_zip", label: "ZIP", type: "text" },
    { key: "service_tier", label: "Service Tier", type: "select", options: { config: "service_tiers" } },
    { key: "primary_producer_id", label: "Assigned Producer", type: "producer", lookup: "owners" },
    { key: "preferred_communication", label: "Preferred Communication", type: "select", options: { static: ["Email", "Phone", "Text", "Mail"] } },
    { key: "contact_permission", label: "Contact Permission", type: "switch" },
    { key: "marketing_permission", label: "Marketing Permission", type: "switch" },
    { key: "referral_source", label: "Referral Source", type: "text" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  household: [
    { key: "display_name", label: "Household Name", type: "text", required: true },
    { key: "email", label: "Primary Email", type: "text" },
    { key: "phone", label: "Primary Phone", type: "text" },
    { key: "address_street", label: "Primary Residence", type: "text" },
    { key: "address_city", label: "City", type: "text" },
    { key: "address_state", label: "State", type: "text" },
    { key: "address_zip", label: "ZIP", type: "text" },
    { key: "service_tier", label: "Service Tier", type: "select", options: { config: "service_tiers" } },
    { key: "primary_producer_id", label: "Assigned Producer", type: "producer", lookup: "owners" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  organization: [
    { key: "display_name", label: "Account Name", type: "text", required: true },
    { key: "legal_name", label: "Legal Entity Name", type: "text" },
    { key: "dba", label: "DBA", type: "text" },
    { key: "parent_company", label: "Parent Company", type: "text" },
    { key: "industry", label: "Industry", type: "text" },
    { key: "naics", label: "NAICS", type: "text" },
    { key: "revenue", label: "Annual Revenue", type: "text" },
    { key: "payroll", label: "Annual Payroll", type: "text" },
    { key: "employee_count", label: "Employee Count", type: "text" },
    { key: "email", label: "Email", type: "text" },
    { key: "phone", label: "Phone", type: "text" },
    { key: "address_street", label: "Street Address", type: "text" },
    { key: "address_city", label: "City", type: "text" },
    { key: "address_state", label: "State", type: "text" },
    { key: "address_zip", label: "ZIP", type: "text" },
    { key: "service_tier", label: "Service Tier", type: "select", options: { config: "service_tiers" } },
    { key: "primary_producer_id", label: "Assigned Producer", type: "producer", lookup: "owners" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
};

export const PROSPECT_EXTRA_FIELDS = [
  { key: "lead_source", label: "Lead Source", type: "select", options: { static: ["Apollo", "Website", "Referral", "Direct Outreach", "Event", "Existing Client", "Producer Generated", "Imported", "Other"] } },
  { key: "interest_level", label: "Interest Level", type: "select", options: { static: ["Hot", "Warm", "Cold", "Nurture", "Unknown"] } },
  { key: "x_date", label: "X-Date / Target Date", type: "date" },
];
// Expose Lead Source / Interest Level / X-Date on the shared party (client & prospect) form.
Object.values(CLIENT_FIELDS).forEach((arr) => {
  if (!arr.some((fld) => fld.key === "lead_source")) arr.push(...PROSPECT_EXTRA_FIELDS.map((x) => ({ ...x })));
});

export const ASSET_FIELDS = {
  Driver: [
    { key: "description", label: "Driver Name", type: "text", required: true },
    { key: "license_state", label: "License State", type: "text" },
    { key: "date_licensed", label: "Date Licensed", type: "date" },
    { key: "relationship", label: "Relationship", type: "text" },
  ],
  Vehicle: [
    { key: "description", label: "Vehicle", type: "text", required: true },
    { key: "year", label: "Year", type: "text" }, { key: "make", label: "Make", type: "text" },
    { key: "model", label: "Model", type: "text" }, { key: "vin", label: "VIN", type: "text" },
    { key: "usage", label: "Usage", type: "select", options: { static: ["Pleasure", "Commute", "Business", "Farm"] } },
    { key: "garaging_address", label: "Garaging Address", type: "text" },
    { key: "lienholder", label: "Lienholder", type: "text" },
  ],
  Residence: [
    { key: "description", label: "Residence Name", type: "text", required: true },
    { key: "address", label: "Address", type: "text" },
    { key: "ownership", label: "Ownership", type: "select", options: { static: ["Owned", "Rented", "Seasonal", "Investment"] } },
    { key: "occupancy", label: "Occupancy", type: "select", options: { static: ["Primary", "Secondary", "Rental", "Vacant"] } },
    { key: "construction", label: "Construction", type: "text" },
    { key: "year_built", label: "Year Built", type: "text" },
    { key: "valuation_reference", label: "Valuation Reference", type: "text" },
    { key: "mortgagee", label: "Mortgagee", type: "text" },
  ],
  Watercraft: [
    { key: "description", label: "Watercraft", type: "text", required: true },
    { key: "watercraft_type", label: "Type", type: "text" }, { key: "hull_reference", label: "Hull Reference", type: "text" },
    { key: "use", label: "Use", type: "text" }, { key: "storage", label: "Storage", type: "text" },
  ],
  "Valuable Item": [
    { key: "description", label: "Description", type: "text", required: true },
    { key: "item_type", label: "Type", type: "text" },
    { key: "scheduled_value", label: "Scheduled Value", type: "text" },
    { key: "appraisal", label: "Appraisal Reference", type: "text" },
  ],
};

export const EXPOSURE_FIELDS = [
  { key: "exposure_type", label: "Exposure Type", type: "select", options: { static: ["Location", "Building", "Payroll", "Class Code", "Fleet Vehicle", "Equipment", "Subcontractor", "Contract"] }, required: true },
  { key: "description", label: "Description", type: "text", required: true },
  { key: "location_name", label: "Location Name", type: "text" },
  { key: "address", label: "Address", type: "text" },
  { key: "class_code", label: "Class Code", type: "text" },
  { key: "payroll", label: "Payroll", type: "text" },
  { key: "revenue", label: "Revenue", type: "text" },
  { key: "employee_count", label: "Employee Count", type: "text" },
  { key: "construction", label: "Construction (COPE)", type: "text" },
  { key: "year_built", label: "Year Built", type: "text" },
  { key: "effective_date", label: "Effective Date", type: "date" },
  { key: "validation_status", label: "Validation Status", type: "select", options: { static: ["Unverified", "In Review", "Verified"] } },
  { key: "source", label: "Source", type: "text" },
];

export const CONTACT_FIELDS = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "role", label: "Role", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "phone", label: "Phone", type: "text" },
];

export const COVERAGE_FIELDS = [
  { key: "coverage_type", label: "Coverage Type / Code", type: "text", required: true },
  { key: "limit", label: "Limit", type: "text" },
  { key: "deductible", label: "Deductible", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "endorsement", label: "Endorsement / Reference", type: "text" },
  { key: "status", label: "Status", type: "select", options: { static: ["Unverified", "Verified", "Pending"] } },
];

export const LIFE_EVENT_FIELDS = [
  { key: "event_type", label: "Event Type", type: "select", options: { config: "life_event_types" }, required: true },
  { key: "event_date", label: "Event Date", type: "date" },
  { key: "notes", label: "Notes", type: "textarea" },
];
